import React, { useState, useEffect } from 'react';
import {
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/solid';
import api from '@monorepo/utils/apiRequest';

const DeviceLimitModal = ({
  isOpen,
  onClose,
  deviceLimitData,
  onLogoutSuccess
}) => {
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [error, setError] = useState(null);
  const [fetchedDevices, setFetchedDevices] = useState(null);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);

  const { message, maxDevices, activeDevices: propsActiveDevices, credentials, canEmergencyRevoke: propsCanEmergencyRevoke, emergencyRevokeCooldownHours: propsEmergencyRevokeCooldownHours, emergencyToken, isOAuth, provider } = deviceLimitData || {};

  // Fetch devices for OAuth flow when modal opens (since devices can't be passed via URL)
  useEffect(() => {
    if (isOpen && isOAuth && emergencyToken && !propsActiveDevices?.length) {
      setIsLoadingDevices(true);
      setError(null);
      api.post('/users/emergency-revoke-devices', { token: emergencyToken })
        .then(data => {
          setFetchedDevices({
            activeDevices: data.active_devices,
            maxDevices: data.max_devices,
            canEmergencyRevoke: data.can_emergency_revoke,
            emergencyRevokeCooldownHours: data.emergency_revoke_cooldown_hours
          });
        })
        .catch(err => {
          setError(err.response?.data?.message || err.message || 'Failed to load devices');
        })
        .finally(() => {
          setIsLoadingDevices(false);
        });
    }
  }, [isOpen, isOAuth, emergencyToken, propsActiveDevices]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedDevice(null);
      setError(null);
      setFetchedDevices(null);
    }
  }, [isOpen]);

  if (!isOpen || !deviceLimitData) return null;

  // Use fetched devices for OAuth flow, or props for password flow
  const activeDevices = fetchedDevices?.activeDevices || propsActiveDevices;
  const canEmergencyRevoke = fetchedDevices?.canEmergencyRevoke ?? propsCanEmergencyRevoke;
  const emergencyRevokeCooldownHours = fetchedDevices?.emergencyRevokeCooldownHours ?? propsEmergencyRevokeCooldownHours;

  const getDeviceIcon = (deviceName) => {
    if (!deviceName) return <ComputerDesktopIcon className="h-8 w-8 text-ink-subtle" />;
    const lower = deviceName.toLowerCase();
    if (lower.includes('iphone') || lower.includes('android') || lower.includes('mobile')) {
      return <DevicePhoneMobileIcon className="h-8 w-8 text-info" />;
    }
    return <ComputerDesktopIcon className="h-8 w-8 text-ink-muted" />;
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const handleLogoutSelectedDevice = async () => {
    if (!selectedDevice) return;

    setIsLoggingOut(true);
    setError(null);

    try {
      if (isOAuth && emergencyToken) {
        // OAuth flow - use token with selected session
        await api.post('/users/emergency-revoke-oauth', {
          token: emergencyToken,
          session_id: selectedDevice
        });
      } else if (credentials) {
        // Password flow - use credentials with selected session
        await api.post('/users/devices/remote-logout', {
          email: credentials.email,
          password: credentials.password,
          session_id: selectedDevice
        });
      } else {
        setError('Unable to revoke device. Please try logging in again.');
        return;
      }
      // Success - trigger retry login
      onLogoutSuccess();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to log out device';
      setError(errorMsg);
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Check if we can perform logout (either have credentials or OAuth token, and not in cooldown)
  const canLogout = (credentials || (isOAuth && emergencyToken)) && canEmergencyRevoke !== false;
  const inCooldown = !canEmergencyRevoke && emergencyRevokeCooldownHours;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop - semi-transparent */}
      <div
        className="fixed inset-0 bg-ink-subtle/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4 relative z-10">
        <div className="relative bg-surface rounded-xl shadow-xl max-w-md w-full p-6 transform transition-all">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-ink-subtle hover:text-ink-muted"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>

          {/* Header */}
          <div className="flex items-center space-x-3 mb-4">
            <div className="flex-shrink-0 w-12 h-12 bg-warning-tint rounded-full flex items-center justify-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-warning" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-ink">
                Device Limit Reached
              </h3>
              <p className="text-sm text-ink-muted">
                Maximum {maxDevices || 2} devices allowed
              </p>
            </div>
          </div>

          {/* Message */}
          <p className="text-ink-muted mb-4">
            Select a device to log out:
          </p>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-danger-tint border border-danger text-danger rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Cooldown warning */}
          {inCooldown && (
            <div className="mb-4 p-3 bg-warning-tint border border-warning rounded-lg">
              <p className="text-sm text-warning">
                You've already used remote logout recently.
              </p>
              <p className="text-xs text-warning mt-1">
                Try again in {emergencyRevokeCooldownHours} hours, or log out from one of your other devices directly.
              </p>
            </div>
          )}

          {/* Loading state for OAuth device fetch */}
          {isLoadingDevices && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-info"></div>
              <span className="ml-3 text-ink-muted">Loading devices...</span>
            </div>
          )}

          {/* Device list */}
          {!isLoadingDevices && activeDevices && activeDevices.length > 0 && (
            <div className="space-y-3 mb-6">
              {activeDevices.map((device) => (
                <div
                  key={device.id}
                  onClick={() => !inCooldown && setSelectedDevice(device.id)}
                  className={`flex items-center p-4 rounded-lg border-2 transition-all ${
                    inCooldown
                      ? 'border-line bg-surface-raised cursor-not-allowed opacity-60'
                      : selectedDevice === device.id
                        ? 'border-info bg-info-tint cursor-pointer'
                        : 'border-line hover:border-line hover:bg-surface-raised cursor-pointer'
                  }`}
                >
                  <div className="flex-shrink-0 mr-4">
                    {getDeviceIcon(device.device_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-ink truncate">
                      {device.device_name || 'Unknown Device'}
                    </p>
                    <p className="text-sm text-ink-muted">
                      Last active: {formatTimeAgo(device.last_active_at)}
                    </p>
                    {device.last_ip && (
                      <p className="text-xs text-ink-subtle">
                        IP: {device.last_ip}
                      </p>
                    )}
                  </div>
                  {!inCooldown && (
                    <div className="flex-shrink-0 ml-2">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedDevice === device.id
                          ? 'border-info bg-info'
                          : 'border-line'
                      }`}>
                        {selectedDevice === device.id && (
                          <div className="w-2 h-2 bg-surface rounded-full" />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          {!inCooldown && !isLoadingDevices && (
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-line text-ink-body rounded-lg hover:bg-surface-raised transition"
              >
                Cancel
              </button>
              <button
                onClick={handleLogoutSelectedDevice}
                disabled={!selectedDevice || isLoggingOut || !canLogout}
                className="flex-1 px-4 py-2 bg-info text-white rounded-lg hover:bg-info transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoggingOut ? 'Logging out...' : 'Log Out Selected'}
              </button>
            </div>
          )}

          {/* Help text */}
          {!inCooldown && !isLoadingDevices && (
            <p className="mt-4 text-xs text-ink-subtle text-center">
              The selected device will be logged out immediately.
              {canEmergencyRevoke && ' You can only do this once every 24 hours.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeviceLimitModal;
